import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import AIBotModal, { AI_BOT_INTERVALS } from '../components/AIBotModal';
import { api } from '../utils/api';
import { aiBotEnabled } from '../utils/features';
import { useI18n } from '../i18n/I18nProvider';

// ── Duration helpers ────────────────────────────────────────────────────────

const INTERVALS = [
  { labelKey: 'projectDetail.intervalOptions.minute1',  ns: 60000000000 },
  { labelKey: 'projectDetail.intervalOptions.minute5',  ns: 300000000000 },
  { labelKey: 'projectDetail.intervalOptions.minute15', ns: 900000000000 },
  { labelKey: 'projectDetail.intervalOptions.minute30', ns: 1800000000000 },
  { labelKey: 'projectDetail.intervalOptions.hour1',    ns: 3600000000000 },
];

const TIMEOUTS = [
  { labelKey: 'projectDetail.timeoutOptions.second5',  ns: 5000000000 },
  { labelKey: 'projectDetail.timeoutOptions.second10', ns: 10000000000 },
  { labelKey: 'projectDetail.timeoutOptions.second30', ns: 30000000000 },
];

function nsLabel(ns, options, t) {
  const v = Number(ns);
  const match = options.find(o => o.ns === v);
  return match ? t(match.labelKey) : `${v}ns`;
}

// ── Notification method metadata ────────────────────────────────────────────

// available: selectable in the UI. Only email/telegram/slack are
// production-ready (PIN-258). logs is testing-only and hidden entirely; the
// rest are shown disabled "(soon)" so the roadmap is visible but not selectable.
const NOTIF_METHODS = [
  { id: 0,  labelKey: 'projectDetail.notifMethods.logs',       hidden: true },
  { id: 1,  labelKey: 'projectDetail.notifMethods.email',      available: true },
  { id: 2,  labelKey: 'projectDetail.notifMethods.telegram',   available: true },
  { id: 3,  labelKey: 'projectDetail.notifMethods.slack',      available: true },
  { id: 4,  labelKey: 'projectDetail.notifMethods.webhook' },
  { id: 5,  labelKey: 'projectDetail.notifMethods.mattermost' },
  { id: 6,  labelKey: 'projectDetail.notifMethods.matrix' },
  { id: 7,  labelKey: 'projectDetail.notifMethods.whatsapp' },
  { id: 10, labelKey: 'projectDetail.notifMethods.sms' },
];

function methodLabel(id, t) {
  const match = NOTIF_METHODS.find(m => m.id === Number(id));
  return match ? t(match.labelKey) : `Method ${id}`;
}

// protoFailing reports whether a protocol's check is currently failing. The
// failure map lives under current_state; read both `state` (snake_case) and
// `State` (Go default) so we tolerate either backend serialisation (PIN-168).
function protoFailing(check, proto) {
  const cs = check.current_state;
  const st = cs && (cs.state || cs.State);
  return !!(st && st[proto]);
}

// protoStatusClass returns the uh-badge colour for an enabled protocol: red when
// that protocol's check is currently failing, otherwise green (PIN-168). We no
// longer grey-out on a missing last_checked_at — an enabled, non-failing check
// reads as healthy, which is what users expect.
function protoStatusClass(check, proto) {
  return protoFailing(check, proto) ? 'danger' : 'ok';
}

// ── Historical availability (PIN-167) ────────────────────────────────────────

const HISTORY_PERIODS = [
  { days: 7,  labelKey: 'projectDetail.history.period7' },
  { days: 30, labelKey: 'projectDetail.history.period30' },
  { days: 90, labelKey: 'projectDetail.history.period90' },
];

// uptimeColour maps an uptime percentage to a theme colour token: green ≥99%,
// amber ≥95%, red below. Used for both the headline number and the sparkline.
function uptimeColour(pct) {
  if (pct >= 99) return 'var(--success-500)';
  if (pct >= 95) return 'var(--warning-500)';
  return 'var(--danger-500)';
}

// UptimeSparkline draws one bar per day from the report's days[] series — bar
// height scales with that day's uptime_pct, colour by the same thresholds. It is
// a hand-rolled inline SVG (no chart library) to keep the bundle small. The
// viewBox lets the browser scale the bars down to the cell's max width.
const UptimeSparkline = ({ days, t }) => {
  if (!days || days.length === 0) return null;
  const H = 22;
  const STEP = 4; // 3px bar + 1px gap, in viewBox units
  const vbW = days.length * STEP;
  return (
    <svg
      width={Math.min(vbW, 180)}
      height={H}
      viewBox={`0 0 ${vbW} ${H}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      role="img"
      aria-label={t('projectDetail.history.uptimeColumn', { days: days.length })}
    >
      {days.map((d, i) => {
        const pct = typeof d.uptime_pct === 'number' ? d.uptime_pct : 100;
        const h = Math.max(2, (pct / 100) * H);
        return (
          <rect key={i} x={i * STEP} y={H - h} width={3} height={h} rx={0.5} fill={uptimeColour(pct)}>
            <title>{`${new Date(d.day).toISOString().slice(0, 10)}: ${pct.toFixed(2)}%`}</title>
          </rect>
        );
      })}
    </svg>
  );
};

// CheckUptimeCell renders the headline uptime %, a sparkline and an incident
// count for one check. `report` is null while loading.
const CheckUptimeCell = ({ report, t }) => {
  if (report === null) return <span className="uh-muted">…</span>;
  if (!report.days || report.days.length === 0) {
    return <span className="uh-muted" style={{ fontSize: 12 }}>{t('projectDetail.history.noData')}</span>;
  }
  const pct = report.uptime_pct || 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 700, color: uptimeColour(pct) }}>{pct.toFixed(2)}%</span>
        <UptimeSparkline days={report.days} t={t} />
      </div>
      {report.incident_count > 0 && (
        <span className="uh-muted" style={{ fontSize: 11 }}>
          {t('projectDetail.history.incidents', { count: report.incident_count })}
        </span>
      )}
    </div>
  );
};

// ── Default check form state ─────────────────────────────────────────────────

const DEFAULT_CHECK = {
  url: '',
  interval: 300000000000,
  ping:   { enabled: true,  max_delay: 500,  max_loss: 1 },
  ssl:    { enabled: true,  ignore_untrusted: false, days_before_expire: 14 },
  domain: { enabled: true,  days_before_expire: 30 },
  http:   { enabled: true,  port: 443, connection_timeout: 5000000000, response_code: 200 },
  robots: { enabled: true },
};

// ── Check modal ──────────────────────────────────────────────────────────────

const CheckModal = ({ projectId, check, onClose, onSaved }) => {
  const { t } = useI18n();
  const editing = !!check;
  const [form, setForm] = useState(editing ? {
    url:      check.url,
    interval: check.interval,
    ping:     { ...DEFAULT_CHECK.ping,   ...check.ping },
    ssl:      { ...DEFAULT_CHECK.ssl,    ...check.ssl },
    domain:   { ...DEFAULT_CHECK.domain, ...check.domain },
    http:     { ...DEFAULT_CHECK.http,   ...check.http },
    // Checks created before PIN-59 have no robots sub-check: keep it off.
    robots:   { enabled: !!check.robots?.enabled },
  } : { ...DEFAULT_CHECK,
    ping:   { ...DEFAULT_CHECK.ping },
    ssl:    { ...DEFAULT_CHECK.ssl },
    domain: { ...DEFAULT_CHECK.domain },
    http:   { ...DEFAULT_CHECK.http },
    robots: { ...DEFAULT_CHECK.robots },
  });
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);

  const set   = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setSub = (key, sub, val) => setForm(f => ({ ...f, [key]: { ...f[key], [sub]: val } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      url:      form.url,
      interval: Number(form.interval),
      ping:     { ...form.ping,   enabled: form.ping.enabled },
      ssl:      { ...form.ssl,    enabled: form.ssl.enabled },
      domain:   { ...form.domain, enabled: form.domain.enabled },
      http:     { ...form.http,   enabled: form.http.enabled, connection_timeout: Number(form.http.connection_timeout) },
      robots:   { enabled: form.robots.enabled },
    };
    const res = editing
      ? await api.updateCheck(projectId, check.ID, payload)
      : await api.createCheck(projectId, payload);
    if (res.ok) {
      onSaved(await res.json());
      onClose();
    } else {
      const d = await res.json().catch(() => ({}));
      setError(d.error === 'email_not_verified' ? t('verifyBanner.text') : t('projectDetail.failedToSaveCheck'));
      setSaving(false);
    }
  };

  return (
    <div className="uh-modal-backdrop">
      <div className="uh-modal" onClick={e => e.stopPropagation()}>
        <div className="uh-modal-title">{editing ? t('projectDetail.editCheck') : t('projectDetail.newCheck')}</div>
        {error && <div className="uh-alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="uh-form-group">
            <label className="uh-label">{t('projectDetail.checkFields.websiteUrl')}</label>
            <input className="uh-input" type="text" placeholder={t('projectDetail.checkFields.urlPlaceholder')}
              value={form.url} onChange={e => set('url', e.target.value)} required />
          </div>
          <div className="uh-form-group">
            <label className="uh-label">{t('projectDetail.checkFields.checkInterval')}</label>
            <select className="uh-select" value={form.interval}
              onChange={e => set('interval', Number(e.target.value))}>
              {INTERVALS.map(i => <option key={i.ns} value={i.ns}>{t(i.labelKey)}</option>)}
            </select>
          </div>

          {/* HTTP */}
          <fieldset className="uh-fieldset">
            <legend>{t('projectDetail.checkFields.httpCheck')}</legend>
            <div className="uh-check-row">
              <input type="checkbox" id="http-en" checked={!!form.http.enabled}
                onChange={e => setSub('http', 'enabled', e.target.checked)} />
              <label htmlFor="http-en">{t('projectDetail.checkFields.enabled')}</label>
            </div>
            <div className="uh-row-2">
              <div className="uh-form-group">
                <label className="uh-label">{t('projectDetail.checkFields.port')}</label>
                <input className="uh-input" type="number" min={1} max={65535}
                  value={form.http.port} onChange={e => setSub('http', 'port', Number(e.target.value))} />
              </div>
              <div className="uh-form-group">
                <label className="uh-label">{t('projectDetail.checkFields.expectedStatusCode')}</label>
                <input className="uh-input" type="number" min={100} max={599}
                  value={form.http.response_code} onChange={e => setSub('http', 'response_code', Number(e.target.value))} />
              </div>
            </div>
            <div className="uh-form-group">
              <label className="uh-label">{t('projectDetail.checkFields.connectionTimeout')}</label>
              <select className="uh-select" value={form.http.connection_timeout}
                onChange={e => setSub('http', 'connection_timeout', Number(e.target.value))}>
                {TIMEOUTS.map(timeout => <option key={timeout.ns} value={timeout.ns}>{t(timeout.labelKey)}</option>)}
              </select>
            </div>
          </fieldset>

          {/* SSL */}
          <fieldset className="uh-fieldset">
            <legend>{t('projectDetail.checkFields.sslCheck')}</legend>
            <div className="uh-check-row">
              <input type="checkbox" id="ssl-en" checked={!!form.ssl.enabled}
                onChange={e => setSub('ssl', 'enabled', e.target.checked)} />
              <label htmlFor="ssl-en">{t('projectDetail.checkFields.enabled')}</label>
            </div>
            <div className="uh-row-2">
              <div className="uh-form-group">
                <label className="uh-label">{t('projectDetail.checkFields.warnBeforeExpiry')}</label>
                <input className="uh-input" type="number" min={1} max={365}
                  value={form.ssl.days_before_expire} onChange={e => setSub('ssl', 'days_before_expire', Number(e.target.value))} />
              </div>
              <div className="uh-form-group" style={{ paddingTop: 22 }}>
                <div className="uh-check-row">
                  <input type="checkbox" id="ssl-ignore" checked={!!form.ssl.ignore_untrusted}
                    onChange={e => setSub('ssl', 'ignore_untrusted', e.target.checked)} />
                  <label htmlFor="ssl-ignore">{t('projectDetail.checkFields.ignoreUntrustedCerts')}</label>
                </div>
              </div>
            </div>
          </fieldset>

          {/* Domain */}
          <fieldset className="uh-fieldset">
            <legend>{t('projectDetail.checkFields.domainExpiryCheck')}</legend>
            <div className="uh-check-row">
              <input type="checkbox" id="domain-en" checked={!!form.domain.enabled}
                onChange={e => setSub('domain', 'enabled', e.target.checked)} />
              <label htmlFor="domain-en">{t('projectDetail.checkFields.enabled')}</label>
            </div>
            <div className="uh-form-group">
              <label className="uh-label">{t('projectDetail.checkFields.warnBeforeExpiry')}</label>
              <input className="uh-input" type="number" min={1} max={365}
                value={form.domain.days_before_expire} onChange={e => setSub('domain', 'days_before_expire', Number(e.target.value))} />
            </div>
          </fieldset>

          {/* robots.txt (PIN-59) */}
          <fieldset className="uh-fieldset">
            <legend>{t('projectDetail.checkFields.robotsCheck')}</legend>
            <div className="uh-check-row">
              <input type="checkbox" id="robots-en" checked={!!form.robots.enabled}
                onChange={e => setSub('robots', 'enabled', e.target.checked)} />
              <label htmlFor="robots-en">{t('projectDetail.checkFields.enabled')}</label>
            </div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>{t('projectDetail.checkFields.robotsHint')}</div>
          </fieldset>

          {/* Ping */}
          <fieldset className="uh-fieldset">
            <legend>{t('projectDetail.checkFields.pingCheck')}</legend>
            <div className="uh-check-row">
              <input type="checkbox" id="ping-en" checked={!!form.ping.enabled}
                onChange={e => setSub('ping', 'enabled', e.target.checked)} />
              <label htmlFor="ping-en">{t('projectDetail.checkFields.enabled')}</label>
            </div>
            <div className="uh-row-2">
              <div className="uh-form-group">
                <label className="uh-label">{t('projectDetail.checkFields.maxDelay')}</label>
                <input className="uh-input" type="number" min={10}
                  value={form.ping.max_delay} onChange={e => setSub('ping', 'max_delay', Number(e.target.value))} />
              </div>
              <div className="uh-form-group">
                <label className="uh-label">{t('projectDetail.checkFields.maxPacketLoss')}</label>
                <input className="uh-input" type="number" min={0} max={100}
                  value={form.ping.max_loss} onChange={e => setSub('ping', 'max_loss', Number(e.target.value))} />
              </div>
            </div>
          </fieldset>

          <div className="uh-modal-footer">
            <button type="button" className="uh-btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="uh-btn uh-btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Telegram chat linking panel (notification method 2) ──────────────────────

// TelegramLinkPanel drives the project-scoped linking flow: the user opens the
// bot via a deep link and the chat is bound server-side, so no chat_id is ever
// typed by hand. It polls the link status until the chat is verified.
const TelegramLinkPanel = ({ projectId }) => {
  const { t } = useI18n();
  const [link, setLink]       = useState(null); // { status, deep_link, group_deep_link, command, expires_at, chat_title, chat_type }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const refresh = useCallback(async () => {
    const res = await api.getTelegramLink(projectId);
    if (res.ok) {
      const s = await res.json();
      setLink(prev => (prev && prev.deep_link && s.status !== 'linked') ? { ...prev, status: s.status } : s);
      if (s.status === 'linked') stopPolling();
    } else {
      setError(t('projectDetail.telegram.loadFailed'));
    }
    setLoading(false);
  }, [projectId, t]);

  useEffect(() => { refresh(); return stopPolling; }, [refresh]);

  const start = async () => {
    setError('');
    setBusy(true);
    const res = await api.startTelegramLink(projectId);
    setBusy(false);
    if (res.status === 503) { setError(t('projectDetail.telegram.unavailable')); return; }
    if (!res.ok) { setError(t('projectDetail.telegram.loadFailed')); return; }
    setLink(await res.json());
    stopPolling();
    pollRef.current = setInterval(refresh, 3000);
  };

  const unlink = async () => {
    setBusy(true);
    const res = await api.deleteTelegramLink(projectId);
    setBusy(false);
    if (res.ok || res.status === 204) { stopPolling(); setLink({ status: 'none' }); }
  };

  if (loading) return <div className="uh-form-group">{t('projectDetail.loading')}</div>;

  const status = link ? link.status : 'none';
  const hasFreshLink = !!(link && link.deep_link);

  return (
    <div className="uh-form-group">
      <label className="uh-label">{t('projectDetail.telegram.title')}</label>
      {error && <div className="uh-alert-error">{error}</div>}

      {status === 'linked' ? (
        <div className="uh-card" style={{ padding: 12 }}>
          <div className="uh-badge ok" style={{ marginBottom: 8 }}><span className="uh-status-dot uh-status-dot--ok" /> {t('projectDetail.telegram.linked')}</div>
          <div style={{ marginBottom: 8 }}>
            {t('projectDetail.telegram.linkedAs', { chat: link.chat_title || link.chat_type || '' })}
          </div>
          <button type="button" className="uh-btn" onClick={unlink} disabled={busy}>
            {t('projectDetail.telegram.unlink')}
          </button>
        </div>
      ) : hasFreshLink ? (
        <div className="uh-card" style={{ padding: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <a className="uh-btn uh-btn-primary" href={link.deep_link} target="_blank" rel="noopener noreferrer">
              {t('projectDetail.telegram.openInTelegram')}
            </a>
            {link.group_deep_link && (
              <a className="uh-btn" href={link.group_deep_link} target="_blank" rel="noopener noreferrer">
                {t('projectDetail.telegram.addToGroup')}
              </a>
            )}
          </div>
          {link.command && (
            <div style={{ marginBottom: 8, fontSize: '0.85em' }}>
              {t('projectDetail.telegram.orCommand')}
              <code style={{ display: 'block', marginTop: 4 }}>{link.command}</code>
            </div>
          )}
          <div className="uh-muted" style={{ fontSize: '0.85em' }}>⏳ {t('projectDetail.telegram.waiting')}</div>
          <button type="button" className="uh-btn" style={{ marginTop: 8 }} onClick={start} disabled={busy}>
            {busy ? t('projectDetail.telegram.connecting') : t('projectDetail.telegram.reconnect')}
          </button>
        </div>
      ) : (
        <>
          <p className="uh-muted" style={{ fontSize: '0.9em', marginBottom: 8 }}>{t('projectDetail.telegram.intro')}</p>
          <button type="button" className="uh-btn uh-btn-primary" onClick={start} disabled={busy}>
            {busy ? t('projectDetail.telegram.connecting') : t('projectDetail.telegram.connect')}
          </button>
        </>
      )}
    </div>
  );
};

// ── Notification data fields by method ───────────────────────────────────────

const NotifDataFields = ({ method, data, onChange, projectId }) => {
  const { t } = useI18n();
  const set = (key, val) => onChange({ ...data, [key]: val });
  switch (Number(method)) {
    case 1: return (
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.emailAddress')}</label>
        <input className="uh-input" type="email" value={data.address || ''}
          onChange={e => set('address', e.target.value)} required />
      </div>
    );
    case 2: return <TelegramLinkPanel projectId={projectId} />;
    case 3: return (<>
      {/* Slack needs a bot token (xoxb-…), not an incoming webhook: in-place
          editing of alerts uses the Web API, which webhooks can't do. */}
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.slackBotToken')}</label>
        <input className="uh-input" type="text" placeholder="xoxb-…" value={data.bot_token || ''}
          onChange={e => set('bot_token', e.target.value)} required />
      </div>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.slackChannel')}</label>
        <input className="uh-input" placeholder="#alerts" value={data.channel || ''}
          onChange={e => set('channel', e.target.value)} required />
      </div>
    </>);
    case 5: return (<>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.webhookUrl')}</label>
        <input className="uh-input" type="url" value={data.webhook_url || ''}
          onChange={e => set('webhook_url', e.target.value)} required />
      </div>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.channelOptional')}</label>
        <input className="uh-input" value={data.channel || ''} onChange={e => set('channel', e.target.value)} />
      </div>
    </>);
    case 4: return (<>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.webhookUrl')}</label>
        <input className="uh-input" type="url" value={data.url || ''}
          onChange={e => set('url', e.target.value)} required />
      </div>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.hmacSecretOptional')}</label>
        <input className="uh-input" value={data.hmac_secret || ''} onChange={e => set('hmac_secret', e.target.value)} />
      </div>
    </>);
    case 6: return (<>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.homeserverUrl')}</label>
        <input className="uh-input" type="url" value={data.url || ''} onChange={e => set('url', e.target.value)} required />
      </div>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.accessToken')}</label>
        <input className="uh-input" value={data.token || ''} onChange={e => set('token', e.target.value)} required />
      </div>
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.roomId')}</label>
        <input className="uh-input" value={data.room_id || ''} onChange={e => set('room_id', e.target.value)} required />
      </div>
    </>);
    case 7: case 10: return (
      <div className="uh-form-group">
        <label className="uh-label">{t('projectDetail.checkFields.phoneNumber')}</label>
        <input className="uh-input" type="tel" value={data.phone || ''}
          onChange={e => set('phone', e.target.value)} required />
      </div>
    );
    default: return null; // method 0 (logs) needs no config
  }
};

// ── Notification modal ───────────────────────────────────────────────────────

const NotifModal = ({ projectId, notif, onClose, onSaved }) => {
  const { t } = useI18n();
  const editing = !!notif;
  const [method,  setMethod]  = useState(editing ? notif.method  : 1);
  const [enabled, setEnabled] = useState(editing ? notif.enabled : true);
  const [data,    setData]    = useState(editing ? (notif.data || {}) : {});
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const handleMethodChange = (v) => { setMethod(Number(v)); setData({}); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    // Telegram chat_id is bound via the linking flow, never sent from the form;
    // keep its data empty so the backend accepts create/update (incl. legacy rows).
    const payload = { method: Number(method), enabled, data: Number(method) === 2 ? {} : data };
    const res = editing
      ? await api.updateNotification(projectId, notif.ID, payload)
      : await api.createNotification(projectId, payload);
    if (res.ok) {
      onSaved(await res.json());
      onClose();
    } else {
      const d = await res.json().catch(() => ({}));
      // Localize known backend errors; never surface the raw English string
      // (PIN-252) — covers the e-mail-address validation from PIN-249.
      const e = d.error || '';
      setError(
        e === 'email_not_verified' ? t('verifyBanner.text')
          : (e.includes('email address') || e.includes('cannot receive mail')) ? t('projectDetail.invalidEmail')
            : t('projectDetail.failedToSaveNotification'),
      );
      setSaving(false);
    }
  };

  return (
    <div className="uh-modal-backdrop" onClick={onClose}>
      <div className="uh-modal" onClick={e => e.stopPropagation()}>
        <div className="uh-modal-title">{editing ? t('projectDetail.editNotification') : t('projectDetail.newNotification')}</div>
        {error && <div className="uh-alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="uh-form-group">
            <label className="uh-label">{t('projectDetail.method')}</label>
            <select className="uh-select" value={method} onChange={e => handleMethodChange(e.target.value)}>
              {NOTIF_METHODS
                // Hide testing-only methods (logs) unless this notification
                // already uses one; keep the current method visible when editing.
                .filter(m => !m.hidden || Number(m.id) === Number(method))
                .map(m => {
                  const locked = !m.available && Number(m.id) !== Number(method);
                  return (
                    <option key={m.id} value={m.id} disabled={locked}>
                      {t(m.labelKey)}{locked ? ` ${t('projectDetail.notifMethods.soon')}` : ''}
                    </option>
                  );
                })}
            </select>
          </div>
          <div className="uh-check-row" style={{ marginBottom: 16 }}>
            <input type="checkbox" id="notif-en" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            <label htmlFor="notif-en">{t('projectDetail.checkFields.enabled')}</label>
          </div>
          <NotifDataFields method={method} data={data} onChange={setData} projectId={projectId} />
          <div className="uh-modal-footer">
            <button type="button" className="uh-btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="uh-btn uh-btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Checks tab ───────────────────────────────────────────────────────────────

const ChecksTab = ({ projectId }) => {
  const { t } = useI18n();
  const [checks, setChecks]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null); // null | 'create' | 'create-ai' | check object
  const [days, setDays]           = useState(30);   // history window: 7 | 30 | 90
  const [reports, setReports]     = useState({});   // checkID -> report | null (loading)
  const [historyAvailable, setHistoryAvailable] = useState(true);

  useEffect(() => {
    api.listChecks(projectId).then(r => r.ok ? r.json() : []).then(data => {
      setChecks(data || []);
      setLoading(false);
    });
  }, [projectId]);

  // Load the availability report for each check. Runs whenever the check set or
  // the selected period changes. A 503 from the server means historical data is
  // not configured — we hide the whole feature gracefully (PIN-167).
  useEffect(() => {
    if (!historyAvailable || checks.length === 0) return;
    let cancelled = false;
    setReports(() => Object.fromEntries(checks.map(c => [c.ID, null])));
    checks.forEach(async (c) => {
      const res = await api.getCheckReport(projectId, c.ID, days);
      if (cancelled) return;
      if (res.status === 503) { setHistoryAvailable(false); return; }
      const data = res.ok ? await res.json() : { days: [] };
      if (!cancelled) setReports(prev => ({ ...prev, [c.ID]: data }));
    });
    return () => { cancelled = true; };
  }, [projectId, checks, days, historyAvailable]);

  const handleSaved = (saved) => {
    setChecks(cs => {
      const idx = cs.findIndex(c => c.ID === saved.ID);
      return idx >= 0 ? cs.map(c => c.ID === saved.ID ? saved : c) : [saved, ...cs];
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('projectDetail.deleteCheck'))) return;
    const res = await api.deleteCheck(projectId, id);
    if (res.ok || res.status === 204) setChecks(cs => cs.filter(c => c.ID !== id));
  };

  if (loading) return <p className="uh-empty">{t('common.loading')}</p>;

  return (
    <>
      <div className="uh-actions" style={{ marginBottom: 16, justifyContent: 'space-between' }}>
        <div className="uh-actions">
          <button className="uh-btn uh-btn-primary" onClick={() => setModal('create')}>{t('projectDetail.addCheck')}</button>
          {aiBotEnabled() && (
            <button className="uh-btn" onClick={() => setModal('create-ai')}>{t('projectDetail.addAIBot')}</button>
          )}
        </div>
        {historyAvailable && checks.length > 0 && (
          <div className="uh-actions">
            {HISTORY_PERIODS.map(p => (
              <button key={p.days}
                className={`uh-btn uh-btn-sm ${days === p.days ? 'uh-btn-primary' : ''}`}
                onClick={() => setDays(p.days)}>
                {t(p.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>
      {checks.length === 0 ? (
        <p className="uh-empty">{t('projectDetail.noChecksYet')}</p>
      ) : (
        <table className="uh-table">
          <thead>
            <tr>
              <th>{t('projectDetail.url')}</th>
              <th>{t('projectDetail.interval')}</th>
              {historyAvailable && <th>{t('projectDetail.history.uptimeColumn', { days })}</th>}
              <th>{t('projectDetail.protocols')}</th>
              <th>{t('projectDetail.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {checks.map(c => (
              <tr key={c.ID}>
                <td data-label={t('projectDetail.url')} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.url}</td>
                <td data-label={t('projectDetail.interval')} style={{ fontSize: 12 }}>{nsLabel(c.interval, [...INTERVALS, ...AI_BOT_INTERVALS], t)}</td>
                {historyAvailable && (
                  <td data-label={t('projectDetail.history.uptimeColumn', { days })}><CheckUptimeCell report={reports[c.ID] ?? null} t={t} /></td>
                )}
                <td data-label={t('projectDetail.protocols')}>
                  <div className="uh-actions">
                    {c.http?.enabled   && <span className={`uh-badge ${protoStatusClass(c, 'http')}`}>{t('projectDetail.protoNames.http')}</span>}
                    {c.ssl?.enabled    && <span className={`uh-badge ${protoStatusClass(c, 'ssl')}`}>{t('projectDetail.protoNames.ssl')}</span>}
                    {c.domain?.enabled && <span className={`uh-badge ${protoStatusClass(c, 'domain')}`}>{t('projectDetail.protoNames.domain')}</span>}
                    {c.ping?.enabled   && <span className={`uh-badge ${protoStatusClass(c, 'ping')}`}>{t('projectDetail.protoNames.ping')}</span>}
                    {c.robots?.enabled && <span className={`uh-badge ${protoStatusClass(c, 'robots')}`}>{t('projectDetail.protoNames.robots')}</span>}
                    {c.ai_bot?.enabled && <span className={`uh-badge ${protoStatusClass(c, 'ai_bot')}`}>{t('projectDetail.protoNames.ai_bot')}</span>}
                  </div>
                </td>
                <td className="uh-td-actions">
                  <div className="uh-actions">
                    <button className="uh-btn uh-btn-sm" onClick={() => setModal(c)}>{t('common.edit')}</button>
                    <button className="uh-btn uh-btn-sm uh-btn-danger" onClick={() => handleDelete(c.ID)}>{t('common.delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (modal === 'create-ai' || modal.ai_bot?.enabled) && (
        <AIBotModal
          projectId={projectId}
          check={modal === 'create-ai' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
      {modal && modal !== 'create-ai' && !modal.ai_bot?.enabled && (
        <CheckModal
          projectId={projectId}
          check={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

// ── Notifications tab ────────────────────────────────────────────────────────

// NotifRow renders one notification with a "Test" button that sends a one-off
// message through the channel and shows the provider result inline.
const NotifRow = ({ projectId, n, onEdit, onDelete }) => {
  const { t } = useI18n();
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null); // { ok, msg }

  const runTest = async () => {
    setTesting(true);
    setResult(null);
    const res = await api.testNotification(projectId, n.ID);
    setTesting(false);
    if (res.ok) {
      setResult({ ok: true, msg: t('projectDetail.notifTest.sent') });
      return;
    }
    const d = await res.json().catch(() => ({}));
    setResult({ ok: false, msg: d.message || t('projectDetail.notifTest.failed') });
  };

  return (
    <tr>
      <td data-label={t('projectDetail.method')}>{methodLabel(n.method, t)}</td>
      <td data-label={t('projectDetail.status')}>
        <span className={`uh-badge ${n.enabled ? 'ok' : 'muted'}`}>
          {n.enabled ? t('common.enabled') : t('common.disabled')}
        </span>
      </td>
      <td className="uh-td-actions">
        <div className="uh-actions">
          <button className="uh-btn uh-btn-sm" onClick={runTest} disabled={testing}>
            {testing ? t('projectDetail.notifTest.sending') : t('projectDetail.notifTest.test')}
          </button>
          <button className="uh-btn uh-btn-sm" onClick={() => onEdit(n)}>{t('common.edit')}</button>
          <button className="uh-btn uh-btn-sm uh-btn-danger" onClick={() => onDelete(n.ID)}>{t('common.delete')}</button>
        </div>
        {result && (
          <div style={{ marginTop: 6, fontSize: '0.85em', display: 'flex', alignItems: 'center', gap: 6, color: result.ok ? 'var(--success-600)' : 'var(--danger-600)' }}>
            <span className={result.ok ? 'uh-status-dot uh-status-dot--ok' : 'uh-status-dot uh-status-dot--danger'} />{result.msg}
          </div>
        )}
      </td>
    </tr>
  );
};

const NotificationsTab = ({ projectId, onCountChange }) => {
  const { t } = useI18n();
  const [notifs, setNotifs]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);

  useEffect(() => {
    api.listNotifications(projectId).then(r => r.ok ? r.json() : []).then(data => {
      const nextNotifs = data || [];
      setNotifs(nextNotifs);
      onCountChange?.(nextNotifs.length);
      setLoading(false);
    });
  }, [onCountChange, projectId]);

  const handleSaved = (saved) => {
    setNotifs(ns => {
      const idx = ns.findIndex(n => n.ID === saved.ID);
      const nextNotifs = idx >= 0 ? ns.map(n => n.ID === saved.ID ? saved : n) : [saved, ...ns];
      onCountChange?.(nextNotifs.length);
      return nextNotifs;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('projectDetail.deleteNotification'))) return;
    const res = await api.deleteNotification(projectId, id);
    if (res.ok || res.status === 204) {
      setNotifs(ns => {
        const nextNotifs = ns.filter(n => n.ID !== id);
        onCountChange?.(nextNotifs.length);
        return nextNotifs;
      });
    }
  };

  if (loading) return <p className="uh-empty">{t('common.loading')}</p>;

  return (
    <>
      <div className="uh-actions" style={{ marginBottom: 16 }}>
        <button className="uh-btn uh-btn-primary" onClick={() => setModal('create')}>{t('projectDetail.addNotification')}</button>
      </div>
      {notifs.length === 0 ? (
        <p className="uh-empty">{t('projectDetail.noNotifications')}</p>
      ) : (
        <table className="uh-table">
          <thead>
            <tr>
              <th>{t('projectDetail.method')}</th>
              <th>{t('projectDetail.status')}</th>
              <th>{t('projectDetail.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {notifs.map(n => (
              <NotifRow key={n.ID} projectId={projectId} n={n} onEdit={setModal} onDelete={handleDelete} />
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <NotifModal
          projectId={projectId}
          notif={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

// ── Project detail page ──────────────────────────────────────────────────────

const ProjectDetailPage = () => {
  const { id }           = useParams();
  const navigate         = useNavigate();
  const { t } = useI18n();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('checks'); // 'checks' | 'notifications'
  const [notificationCount, setNotificationCount] = useState(null);
  const [editName, setEditName]   = useState('');
  const [editingName, setEditingName] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [renameError, setRenameError] = useState('');

  const loadProject = useCallback(() => {
    api.getProject(id).then(r => r.ok ? r.json() : null).then(p => {
      setProject(p);
      if (p) setEditName(p.ProjectName);
      setLoading(false);
    });
  }, [id]);

  useEffect(() => { loadProject(); }, [loadProject]);

  useEffect(() => {
    api.listNotifications(id).then(r => r.ok ? r.json() : []).then(data => {
      setNotificationCount((data || []).length);
    });
  }, [id]);

  const toggleEnabled = async () => {
    const res = await api.updateProject(id, { Enabled: !project.Enabled });
    if (res.ok) setProject(p => ({ ...p, Enabled: !p.Enabled }));
  };

  const saveRename = async (e) => {
    e.preventDefault();
    setSaving(true);
    setRenameError('');
    const res = await api.updateProject(id, { ProjectName: editName });
    if (res.ok) {
      const updated = await res.json();
      setProject(updated);
      setEditName(updated.ProjectName);
      setEditingName(false);
    } else {
      // A failed rename used to be silent (PIN-332).
      const d = await res.json().catch(() => ({}));
      setRenameError(d.error === 'project_name_taken' ? t('projectModal.errors.taken') : t('projectDetail.renameFailed'));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(t('projectDetail.deleteConfirm', { name: project.ProjectName }))) return;
    const res = await api.deleteProject(id);
    if (res.ok || res.status === 204) navigate('/projects', { replace: true });
  };

  const header = (
    <>
      <h1 className="uh-title" style={{ cursor: 'pointer' }} onClick={() => navigate('/projects')}>
        {t('projectDetail.backToProjects')}
      </h1>
    </>
  );

  if (loading) return <Layout sidebar={<Sidebar />} header={header}><p className="uh-empty">{t('common.loading')}</p></Layout>;
  if (!project) return <Layout sidebar={<Sidebar />} header={header}><p className="uh-empty">{t('projectDetail.projectNotFound')}</p></Layout>;

  return (
    <Layout sidebar={<Sidebar />} header={header}>
      {/* Project header */}
      <div className="uh-card" style={{ padding: '18px 24px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {editingName ? (
          <form onSubmit={saveRename} style={{ display: 'flex', gap: 8, flex: 1 }}>
            <input className="uh-input" value={editName}
              onChange={e => setEditName(e.target.value)} autoFocus style={{ maxWidth: 300 }} />
            <button type="submit" className="uh-btn uh-btn-primary uh-btn-sm" disabled={saving}>{t('common.save')}</button>
            <button type="button" className="uh-btn uh-btn-sm" onClick={() => { setEditingName(false); setEditName(project.ProjectName); setRenameError(''); }}>{t('common.cancel')}</button>
            {renameError && <div className="uh-alert-error" style={{ margin: 0 }}>{renameError}</div>}
          </form>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, flexWrap: 'wrap' }}>
            <span
              className={`uh-status-dot uh-status-dot--${project.Enabled ? 'ok' : 'muted'}`}
              aria-hidden="true"
            />
            <span style={{ fontWeight: 700, fontSize: 18 }}>{project.ProjectName}</span>
            <span className={`uh-badge ${project.Enabled ? 'ok' : 'muted'}`}>
              {project.Enabled ? t('common.active') : t('common.paused')}
            </span>
            <button className="uh-btn uh-btn-sm" onClick={() => setEditingName(true)}>{t('projectDetail.rename')}</button>
          </div>
        )}
        <div className="uh-actions">
          <button className="uh-btn uh-btn-sm" onClick={toggleEnabled}>
            {project.Enabled ? t('projectDetail.pause') : t('projectDetail.resume')}
          </button>
          <button className="uh-btn uh-btn-sm uh-btn-danger" onClick={handleDelete}>{t('projectDetail.deleteProject')}</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="uh-tabs">
        <button className={`uh-tab ${tab === 'checks' ? 'active' : ''}`} onClick={() => setTab('checks')}>
          {t('projectDetail.checks')}
        </button>
        <button className={`uh-tab ${tab === 'notifications' ? 'active' : ''}`} onClick={() => setTab('notifications')}>
          {t('projectDetail.notifications')}
          {notificationCount === 0 && (
            <>
              <span className="uh-badge danger" style={{ marginLeft: 8 }}>{t('projectDetail.setUp')}</span>
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: '#b45309' }}>
                {t('projectDetail.notificationsNotConfigured')}
              </span>
            </>
          )}
        </button>
      </div>

      {tab === 'checks'        && <ChecksTab        projectId={Number(id)} />}
      {tab === 'notifications' && <NotificationsTab projectId={Number(id)} onCountChange={setNotificationCount} />}
    </Layout>
  );
};

export default ProjectDetailPage;
